import type { CSSProperties } from 'react'
import React from 'react'
import classNames from 'classnames'

import {
  AttioGreyIcon,
  FileIcon,
  GitHubGreyIcon,
  GlobeIcon,
  InlineCodeIcon,
} from '@slite/icons'
import { SuperSource, superSourceToIconUrl } from '@slite/search-client'

import { CustomNoteIcon } from '../CustomNoteIcon/CustomNoteIcon'
import type { NoteIconVariant } from '../CustomNoteIcon/types'

export type SuperSourceIconProps = {
  sourceType: SuperSource
  className?: string
  backgroundClassName?: string
  style?: CSSProperties
  sliteIconShape?: string | null
  sliteIconColor?: string | null
  sliteIconVariant?: NoteIconVariant | null
  notionIcon?: string | null // render icon from Notion sources
  isExpanded?: boolean | null
  size?: React.CSSProperties['height']
  expanded?: boolean
  variant?: 'small' | 'normal'
  isCold?: boolean
  isMonochrome?: boolean
  isSourceTypeIcon?: boolean
  ref?: React.Ref<HTMLElement>
}

function getIconContent({
  sourceType,
  notionIcon,
  isSourceTypeIcon,
  size,
  isMonochrome,
}: {
  sourceType: SuperSource
  notionIcon: string | null
  size: React.CSSProperties['height']
  isSourceTypeIcon?: boolean
  isMonochrome?: boolean
}) {
  const wrapperClassName = 'flexbox-inline flexbox-centered position-relative'

  if (sourceType === SuperSource.NOTION && notionIcon) {
    const isUrl = notionIcon?.startsWith('https://')
    return (
      <span
        className={wrapperClassName}
        style={{ width: size, height: size, fontSize: size }}
      >
        {isUrl ? (
          <img src={notionIcon} style={{ width: size, height: size }} />
        ) : (
          notionIcon // if not a URL, it's a utf8
        )}
      </span>
    )
  } else if (sourceType === SuperSource.WEBSITE) {
    return (
      <span className={wrapperClassName}>
        <GlobeIcon size={size} />
      </span>
    )
  } else if (sourceType === SuperSource.FILE) {
    return (
      <span className={wrapperClassName}>
        <FileIcon size={size} />
      </span>
    )
  } else if (sourceType === SuperSource.CUSTOM) {
    return (
      <span className={wrapperClassName}>
        <InlineCodeIcon size={size} />
      </span>
    )
  } else if (sourceType === SuperSource.MCP) {
    return (
      <span className={wrapperClassName}>
        <InlineCodeIcon size={size} />
      </span>
    )
  } else if (sourceType !== SuperSource.SLITE || isSourceTypeIcon) {
    if (
      isMonochrome &&
      [SuperSource.GITHUB, SuperSource.ATTIO].includes(sourceType)
    ) {
      switch (sourceType) {
        case SuperSource.GITHUB:
          return <GitHubGreyIcon size={size} />
        case SuperSource.ATTIO:
          return <AttioGreyIcon size={size} />
        default:
          return
      }
    }

    const url = superSourceToIconUrl(sourceType)
    return (
      <span
        className={wrapperClassName}
        style={{ width: size, height: size, fontSize: size }}
      >
        {<img src={url} style={{ width: size, height: size }} />}
      </span>
    )
  }

  return null
}

export function SuperSourceIcon({
  sourceType,
  sliteIconShape = null,
  sliteIconColor = null,
  sliteIconVariant = null,
  notionIcon = null,
  size = 16,
  variant = 'normal',
  className,
  style,
  expanded = false,
  isCold = false,
  isMonochrome = false,
  isSourceTypeIcon = false,
  ref,
}: SuperSourceIconProps) {
  const iconContent = getIconContent({
    sourceType,
    notionIcon,
    isSourceTypeIcon,
    size,
    isMonochrome,
  })

  if (iconContent) {
    return (
      <span
        ref={ref}
        className={classNames(
          className,
          'flexbox-inline flexbox-centered position-relative',
          isCold && 'shade-30'
        )}
        style={{ ...style, width: size, height: size }}
      >
        {iconContent}
      </span>
    )
  }
  return (
    <CustomNoteIcon
      ref={ref}
      iconShape={sliteIconShape}
      iconColor={sliteIconColor}
      iconVariant={sliteIconVariant}
      size={size}
      className={className}
      variant={variant}
      expanded={expanded}
      style={style}
      isCold={isCold}
    />
  )
}
